import { useState, useCallback, useRef } from 'preact/hooks'
import { landingPagesApi } from '@/api/landingPages'
import { useToast } from '@/hooks/useToast'
import type {
    LandingPageViewDto,
    LandingPageGenerateRequestDto,
    LandingPageSettingsUpdateDto,
    LandingPageContextDto,
    LandingPageContextUpdateDto,
    LandingPageDesignAnswerDto,
    LandingPageMode,
    ThankYouPageSettingsUpdateDto,
    PageMetaUpdateDto,
} from '@/types/landingPage'

interface UseLandingPageParams {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
}

export interface UseLandingPageResult {
    landingPage: LandingPageViewDto | null
    setLandingPage: (lp: LandingPageViewDto | null) => void
    html: string | null
    context: LandingPageContextDto | null
    loading: boolean
    contextLoading: boolean
    generating: boolean
    error: string | null
    load: () => Promise<LandingPageViewDto | null>
    loadDraft: () => Promise<void>
    loadContext: () => Promise<void>
    updateContext: (dto: LandingPageContextUpdateDto) => Promise<void>
    /** Answers Webster's outstanding design questions and restarts generation. */
    submitDesignAnswers: (
        answers: LandingPageDesignAnswerDto[],
        note: string | null,
        letWebsterDecide?: boolean,
    ) => Promise<void>
    createAndGenerate: (request: LandingPageGenerateRequestDto) => Promise<void>
    regenerate: (prompt?: string) => Promise<void>
    updateSettings: (dto: LandingPageSettingsUpdateDto) => Promise<void>
    applyFont: (headingFamily: string | null, bodyFamily: string | null) => Promise<boolean>
    thankYouHtml: string | null
    loadThankYouDraft: () => Promise<void>
    updateThankYouSettings: (dto: ThankYouPageSettingsUpdateDto) => Promise<void>
    updatePageMeta: (dto: PageMetaUpdateDto) => Promise<void>
    regenerateThankYou: (prompt?: string) => Promise<void>
    deleteLandingPage: () => Promise<void>
    setMode: (mode: LandingPageMode, externalUrl?: string) => Promise<LandingPageViewDto | null>
}

export function useLandingPage({ clientCompanyId, workspaceId, campaignId, webId }: UseLandingPageParams): UseLandingPageResult {
    const { showError, showSuccess } = useToast()

    const [landingPage, setLandingPage] = useState<LandingPageViewDto | null>(null)
    const [html, setHtml] = useState<string | null>(null)
    const [thankYouHtml, setThankYouHtml] = useState<string | null>(null)
    const [context, setContext] = useState<LandingPageContextDto | null>(null)
    const [loading, setLoading] = useState(false)
    const [contextLoading, setContextLoading] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const hasLoadedOnce = useRef(false)

    const load = useCallback(async (): Promise<LandingPageViewDto | null> => {
        // Only show loading spinner on initial load, not on reloads after generation
        if (!hasLoadedOnce.current) setLoading(true)
        setError(null)
        const res = await landingPagesApi.getLandingPage(workspaceId, webId)
        if (res.data) {
            setLandingPage(res.data)
        } else if (res.status !== 404) {
            const message = res.error || 'Failed to load landing page'
            setError(message)
            showError('Load failed', message)
        }
        if (!hasLoadedOnce.current) {
            hasLoadedOnce.current = true
            setLoading(false)
        }
        return res.data ?? null
    }, [workspaceId, webId])

    const loadDraft = useCallback(async () => {
        const res = await landingPagesApi.getLandingPageDraft(workspaceId, webId)
        if (res.data) {
            setHtml(res.data as string)
        } else if (res.status !== 404 && res.error) {
            showError('Failed to load preview', res.error)
        }
    }, [workspaceId, webId])

    const loadContext = useCallback(async () => {
        setContextLoading(true)
        const res = await landingPagesApi.getContext(clientCompanyId, workspaceId, campaignId, webId)
        if (res.data) {
            setContext(res.data)
        } else if (res.error) {
            console.warn('Failed to load context:', res.error)
        }
        setContextLoading(false)
    }, [clientCompanyId, workspaceId, campaignId, webId])

    const updateContext = useCallback(async (dto: LandingPageContextUpdateDto) => {
        const res = await landingPagesApi.updateContext(workspaceId, webId, dto)
        if (res.data) {
            setLandingPage(res.data)
            if (res.data.Context) {
                setContext(res.data.Context)
            }
        } else {
            showError('Update failed', res.error || 'Unable to update context')
        }
    }, [workspaceId, webId])

    /**
     * Answers Webster's outstanding design questions and restarts generation. The server
     * requeues the build, so the LP comes back on Generating rather than finished.
     */
    const submitDesignAnswers = useCallback(async (
        answers: LandingPageDesignAnswerDto[],
        note: string | null,
        letWebsterDecide = false,
    ) => {
        const etag = landingPage?.ETag
        if (!etag) {
            showError('Update failed', 'Landing page not loaded')
            return
        }
        const res = await landingPagesApi.submitDesignAnswers(workspaceId, webId, {
            ETag: etag,
            Answers: answers,
            Note: note,
            LetWebsterDecide: letWebsterDecide,
        })
        if (res.data) {
            setLandingPage(res.data)
            if (res.data.Context) setContext(res.data.Context)
        } else {
            showError('Could not send your answers', res.error || 'Unable to submit design answers')
        }
    }, [workspaceId, webId, landingPage?.ETag])

    const createAndGenerate = useCallback(async (request: LandingPageGenerateRequestDto) => {
        setGenerating(true)
        setHtml(null)
        setThankYouHtml(null)
        setError(null)
        const createRes = await landingPagesApi.createLandingPage(clientCompanyId, workspaceId, campaignId, webId, request)
        if (createRes.error) {
            setGenerating(false)
            showError('Create failed', createRes.error)
            return
        }
        if (createRes.data) {
            setLandingPage(createRes.data)
            if (createRes.data.Context) {
                setContext(createRes.data.Context)
            }
            // Don't load HTML here — generation is async (~2 min).
            // SignalR onStreamResult will load HTML when the AI finishes.
        }
        setGenerating(false)
    }, [clientCompanyId, workspaceId, campaignId, webId])

    /** Fire-and-forget for 202-Accepted endpoints where SignalR drives state updates. */
    const check202 = useCallback((res: { error?: string; status?: number }, errorTitle: string) => {
        if (res.error && res.status !== 202) {
            showError(errorTitle, res.error || 'Request failed')
        }
    }, [])

    const regenerate = useCallback(async (prompt?: string) => {
        setGenerating(true)
        setHtml(null)
        const res = await landingPagesApi.generateLandingPage(workspaceId, webId, prompt)
        check202(res, 'Update failed')
        setGenerating(false)
    }, [workspaceId, webId, check202])

    const updateSettings = useCallback(async (dto: LandingPageSettingsUpdateDto) => {
        const res = await landingPagesApi.updateSettings(workspaceId, webId, dto)
        if (res.data) {
            setLandingPage(res.data)
            showSuccess('Settings updated', 'Landing page settings have been saved.')
        } else {
            showError('Update failed', res.error || 'Unable to update settings')
        }
    }, [workspaceId, webId])

    // Applies a font choice to an existing page. The server re-bakes the draft, so we
    // reload it to update the preview iframe instantly (no regeneration). Post-generation
    // only — pre-generation the choice rides into the Generate request body instead.
    const applyFont = useCallback(async (headingFamily: string | null, bodyFamily: string | null) => {
        const res = await landingPagesApi.applyFont(workspaceId, webId, {
            HeadingFamily: headingFamily,
            BodyFamily: bodyFamily,
        })
        if (res.data) {
            setLandingPage(res.data)
            await loadDraft()
            return true
        }
        showError('Font change failed', res.error || 'Unable to apply the selected font')
        return false
    }, [workspaceId, webId, loadDraft])

    const loadThankYouDraft = useCallback(async () => {
        const res = await landingPagesApi.getThankYouPageDraft(workspaceId, webId)
        if (res.data) {
            setThankYouHtml(res.data as string)
        } else if (res.status !== 404 && res.error) {
            showError('Failed to load thank you page preview', res.error)
        }
    }, [workspaceId, webId])

    const updateThankYouSettings = useCallback(async (dto: ThankYouPageSettingsUpdateDto) => {
        const res = await landingPagesApi.updateThankYouSettings(workspaceId, webId, dto)
        if (res.data) {
            setLandingPage(res.data)
            showSuccess('Settings updated', 'Thank you page settings have been saved.')
        } else {
            showError('Update failed', res.error || 'Unable to update thank you settings')
        }
    }, [workspaceId, webId])

    const updatePageMeta = useCallback(async (dto: PageMetaUpdateDto) => {
        const res = await landingPagesApi.updatePageMeta(workspaceId, webId, dto)
        if (res.data) {
            setLandingPage(res.data)
        } else {
            showError('Update failed', res.error || 'Unable to update page meta')
        }
    }, [workspaceId, webId])

    const regenerateThankYou = useCallback(async (prompt?: string) => {
        const res = await landingPagesApi.regenerateThankYou(workspaceId, webId, prompt)
        check202(res, 'Generate failed')
    }, [workspaceId, webId, check202])

    const setMode = useCallback(async (mode: LandingPageMode, externalUrl?: string): Promise<LandingPageViewDto | null> => {
        const res = await landingPagesApi.setMode(workspaceId, campaignId, webId, {
            ETag: landingPage?.ETag,
            Mode: mode,
            ExternalUrl: externalUrl,
        })
        if (res.data) {
            setLandingPage(res.data)
            showSuccess(
                mode === 'External' ? 'External URL set' : 'Switched to Builder',
                mode === 'External'
                    ? 'Landing page now uses your custom URL.'
                    : 'Landing page switched back to the AI builder.',
            )
            return res.data
        }
        showError('Update failed', res.error || 'Unable to update landing page mode')
        return null
    }, [workspaceId, campaignId, webId, landingPage?.ETag])

    const deleteLandingPage = useCallback(async () => {
        const res = await landingPagesApi.deleteLandingPage(workspaceId, webId)
        if (res.status === 204 || res.status === 200) {
            setLandingPage(null)
            setHtml(null)
            setThankYouHtml(null)
            setContext(null)
            showSuccess('Deleted', 'Landing page and all versions have been removed.')
        } else {
            showError('Delete failed', res.error || 'Unable to delete landing page')
        }
    }, [workspaceId, webId])

    return {
        landingPage,
        setLandingPage,
        html,
        context,
        loading,
        contextLoading,
        generating,
        error,
        load,
        loadDraft,
        loadContext,
        updateContext,
        submitDesignAnswers,
        createAndGenerate,
        regenerate,
        thankYouHtml,
        loadThankYouDraft,
        updateThankYouSettings,
        updatePageMeta,
        regenerateThankYou,
        updateSettings,
        applyFont,
        deleteLandingPage,
        setMode,
    }
}
